import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { take } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { AccountSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import type { HelpTranslationLinkMapKey } from '@core/help-articles-legacy';
import { HelpTranslationLinkMap } from '@core/help-articles-legacy';
import { I18nService } from '@core/i18n-legacy';
import { TellentCurrentUserSelectors } from '@core/tellent-current-user-data';

import type { Step } from '../types';
import { WalkthroughFeatureFilter } from '../types';

@Injectable({ providedIn: 'root' })
export class WalkthroughFacadeService {
  public params$ = this.router.events.pipe(
    filter(evt => evt instanceof NavigationEnd),
    switchMap(() => this.activatedRoute.queryParams),
    map(params => params?.walkthrough),
    filterBoolean,
  );

  public queryParams = this.activatedRoute.snapshot.queryParams;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private i18n: I18nService,
    private store: Store,
  ) {}

  public openHelpArticleLink(key: HelpTranslationLinkMapKey): void {
    window.open(this.i18n.translate(HelpTranslationLinkMap[key]), '_blank');
  }

  public getFilteredStepsByAbilities(steps: Step[]): Observable<Step[]> {
    return this.store.select(AbilitiesSelectors.abilityMap).pipe(
      filter(Boolean),
      map(abilityMap =>
        steps
          .filter(({ abilities }) => !abilities || abilities.every(ability => abilityMap[ability]))
          .map((step, index) => ({ ...step, position: index + 1 })),
      ),
      take(1),
    );
  }

  public filterByCareersSiteFeature(steps: Step[]): Observable<Step[]> {
    return this.store.select(AccountSelectors.currentCompany).pipe(
      filterBoolean,
      map(currentCompany => currentCompany.careersSite.enabled),
      map(enabled =>
        steps
          .filter(step =>
            step.features?.includes(WalkthroughFeatureFilter.CareersSite) ? enabled : true,
          )
          .map((step, index) => ({ ...step, position: index + 1 })),
      ),

      take(1),
    );
  }

  public memberHasNoSeats(): Observable<boolean> {
    return this.store
      .select(TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe)
      .pipe(
        map(member => member?.seats.length === 0),
        take(1),
      );
  }
}
